import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@renderer/components/bahela/Button'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'

interface ConsentModalProps {
  onConsent: (consent: boolean) => void
  isVisible: boolean
  stripPreview?: string
  mode?: 'capture' | 'record'
  timeout?: number // Optional timeout in seconds, defaults to 15
}

const ConsentModal: React.FC<ConsentModalProps> = ({
  onConsent,
  isVisible,
  stripPreview,
  mode,
  timeout = 30
}) => {
  const { i18n } = useTranslation()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect((): (() => void) => {
    if (isVisible && timeout > 0) {
      // Start invisible timeout
      timeoutRef.current = setTimeout(() => {
        // Auto-decline consent after timeout
        onConsent(false)
      }, timeout * 1000)
    }

    // Cleanup timeout on unmount or when visibility changes
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isVisible, timeout, onConsent])

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-8">
      {/* Semi-transparent backdrop */}
      <div
        className="absolute inset-0 bg-black/90"
        style={{ backgroundImage: `url(${bgLayer})` }}
      />

      {/* Modal Content */}
      <div className="relative bg-methadone/95 backdrop-blur-md rounded-3xl shadow-2xl border-4 border-uriYellow p-8 max-w-4xl w-full">
        <div className="flex gap-8 items-center">
          {/* Left side - Strip Preview */}
          {stripPreview && (
            <div className="flex-shrink-0">
              {mode === 'record' ? (
                <video
                  src={stripPreview}
                  controls
                  className="w-64 h-auto object-contain drop-shadow-2xl rounded-lg"
                />
              ) : (
                <img
                  src={stripPreview}
                  alt="Photo Strip Preview"
                  className="w-64 h-auto object-contain drop-shadow-2xl"
                />
              )}
            </div>
          )}

          {/* Right side - Content */}
          <div className="flex-1 flex flex-col items-center text-center">
            {/* Title */}
            <h1 className="text-[40px] font-serif-bahela text-uriYellow mb-3 drop-shadow-2xl">
              {i18n.language === 'id' ? 'Persetujuan Publikasi' : 'Publication Consent'}
            </h1>

            {/* Question */}
            <p className="text-[26px] text-white font-body-bahela leading-relaxed mb-6 drop-shadow-lg">
              {i18n.language === 'id'
                ? 'Bolehkah kami menggunakan foto Anda untuk publikasi?'
                : 'Can we use your photo for publication?'}
            </p>

            {/* Action Buttons */}
            <div className="flex gap-4 w-full">
              <Button
                onClick={(): void => onConsent(true)}
                className="flex-1 text-[26px] !py-4 !px-6"
                textColor="methadone"
              >
                {i18n.language === 'id' ? 'Ya' : 'Yes'}
              </Button>
              <Button
                onClick={(): void => onConsent(false)}
                className="flex-1 text-[26px] !py-4 !px-6"
                textColor="methadone"
              >
                {i18n.language === 'id' ? 'Tidak' : 'No'}
              </Button>
            </div>

            {/* Footer Note */}
            <div className="mt-4">
              <p className="text-[14px] text-white/80 font-body-bahela drop-shadow-lg">
                {i18n.language === 'id'
                  ? 'Hubungi bahela.id untuk mengubah preferensi'
                  : 'Contact bahela.id to change preference'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConsentModal
